import { useState, useEffect, useRef } from 'react';
import { createPortal } from "react-dom";
import Loader from '../../../components/globalloaders/Loader';
import { useLoading } from '../../../components/globalloaders/LoadingContext';
import { toast } from 'react-toastify';
import { Bars3Icon, EllipsisVerticalIcon, ChevronLeftIcon, ChevronRightIcon, UserIcon, XMarkIcon, Square2StackIcon } from "@heroicons/react/24/outline";
import { encryptPayload, decryptPayload, formatDateTime, formatCurrency, formatTableDateTime, formatLabel, formatCnic } from '../../../constants/helpers';
import ConfirmDialog from '../../../components/dialogbox/ConfirmDialog';
import OpCodeDialog from "../../../components/dialogbox/OpCodeDialog";
import EntitySelector from '../components/entry/EntitySelector';
import api from '../../../utils/auth/api';
import FallBack from '../../../components/dialogbox/FallBack';
import jsPDF from "jspdf";
import { userRoleOptions, employeeMediaRoles } from '../../../constants/options';

const Account = ({ setIsMobileOpen }) => {

    const mediaURI = `${process.env.REACT_APP_URI}/stream/internal-media`;

    const { isLoading, setIsLoading } = useLoading();

    const [loaders, setLoaders] = useState({
        fetch: false,
        update: false,
        delete: false,
    });
    const [dialog, setDialog] = useState({
        open: false,
        type: "info",
        title: "",
        message: "",
        confirmText: "",
        action: () => { },
    });
    const [opCodeDialog, setOpCodeDialog] = useState({
        open: false,
        id: null,
    });

    const [accountData, setAccountData] = useState([]);
    const [selectedUser, setSelectedUser] = useState({
        id: null,
        role: null,
    });

    // Menu State
    const [showMenu, setShowMenu] = useState(false);
    const attachmentRefs = useRef({});

    // Menu List State
    const [menuState, setMenuState] = useState({
        open: false,
        rowId: null,
        position: { top: 0, left: 0 },
        rowData: null,
    });
    const openMenu = ({ id, position, row }) => {
        setMenuState({
            open: true,
            rowId: id,
            position,
            rowData: row,
        });
    };
    const closeMenu = () => {
        setMenuState({
            open: false,
            rowId: null,
            position: { top: 0, left: 0 },
            rowData: null,
        });
    };

    // Pagination States
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 15;
    const [totalPages, setTotalPages] = useState(1);
    const paginatedData = accountData;
    const [totalBackendPg, setTotalBackendPg] = useState(1);
    const startIndex = (currentPage - 1) * itemsPerPage + 1;
    const endIndex = Math.min(currentPage * itemsPerPage, totalBackendPg);

    // Fetch All API
    useEffect(() => {
        const fetchData = async (page = 1) => {
            setLoaders(prev => ({ ...prev, fetch: true }));
            try {
                const response = await api.get(`${process.env.REACT_APP_URI}/account/get-all`, {
                    params: { page, limit: itemsPerPage },
                });
                if (response.data.success) {
                    const decryptedData = decryptPayload(response.data.encryptedData);
                    setAccountData(decryptedData.data);
                    setTotalBackendPg(decryptedData.total);
                    setTotalPages(Math.ceil(decryptedData.total / itemsPerPage));
                }
                else {
                    toast.error(response.data.message);
                }
            } catch (error) {
                console.error('Error fetching accounts:', error);
            } finally {
                setLoaders(prev => ({ ...prev, fetch: false }));
                return;
            }
        };
        fetchData(currentPage);
    }, [currentPage]);

    // Delete API By ID
    const handleDelete = async (id, opcode) => {
        if (isLoading) return;
        setIsLoading(true);
        const payload = encryptPayload({ id, opcode });
        try {
            const response = await api.delete(`${process.env.REACT_APP_URI}/account/delete`, { data: { data: payload } });
            if (response.data.success) {
                setAccountData((prevData) => prevData.filter((data) => data._id !== id));
                toast.success(response.data.message);
            }
        }
        catch (error) {
            console.log("Error occur while deleting user account:", error);
            if (error.response && error.response.data) {
                const data = error.response.data;
                toast.error(data.message || "Delete User Account error occurred!");
            } else {
                toast.error("Unable to Delete User Account Network Error, please try again!");
            }
        }
        finally {
            setIsLoading(false);
            return;
        }
    };

    // Individual Credential PDF
    const generateCredentialPDF = async (data) => {
        const doc = new jsPDF({ unit: "mm", format: "a4" });
        const pageWidth = 210;
        const margin = 6;

        const cardWidth = (pageWidth - margin * 2) / 1; // one per row
        const cardHeight = 40;

        let x = margin;
        let y = 20;

        // === Header ===
        const addHeader = () => {
            // Brand title (top left)
            doc.setFont("helvetica", "bold");
            doc.setFontSize(14);
            doc.setTextColor(25, 25, 25);
            doc.text("MBEOWS Internal Control", margin, 6);

            // Tagline (under brand, lighter)
            doc.setFont("helvetica", "italic");
            doc.setFontSize(9);
            doc.setTextColor(100, 100, 100);
            doc.text("Smart by Logic. Built for You.", margin, 11);

            const timestamp = new Date().toLocaleString();
            doc.setFont("helvetica", "italic");
            doc.setFontSize(8);
            doc.setTextColor(80);
            const footerText = `Generated by MBEOWS  •  ${timestamp}`;
            doc.text(footerText, 130, 11, { align: "left" });

            // Divider line to separate header
            doc.setDrawColor(220);
            doc.line(margin, 13, pageWidth - margin, 13);
        };

        addHeader();

        // Border
        doc.setDrawColor(180);
        doc.roundedRect(x, y, cardWidth, cardHeight, 3, 3);

        // User info
        doc.setFontSize(9);
        let textY = y + 10;

        // Helper for label + value
        const drawLabelValue = (label, value) => {
            doc.setFont("helvetica", "bold");
            doc.setTextColor(25, 25, 25);
            doc.text(`${label}:`, x + 8, textY);

            doc.setFont("helvetica", "normal");
            doc.setTextColor(60, 60, 60);
            doc.text(`${value || "-"}`, x + 30, textY);

            textY += 5; // move down for next line
        };

        drawLabelValue("User ID", data.userId);
        drawLabelValue("Role", formatLabel(data.role));
        drawLabelValue("Username", data.username);
        drawLabelValue("Password", data.password);

        textY += 4;

        doc.save(`${data.userId}_${formatLabel(data.role)}_Credential.pdf`);
    };

    // Update State & API
    const updateInitialState = {
        salary: "",
    };
    const [updateData, setUpdateData] = useState(updateInitialState);
    const [isUpdateDialog, setUpdateDialog] = useState(false);
    const [updateMode, setUpdateMode] = useState("pwd");
    const onChangeUpdate = (e) => {
        const { name, value } = e.target;
        const digits = name === "salary" ? value.replace(/\D/g, "") : value;
        setUpdateData({ ...updateData, [name]: digits });
    };
    const handleUpdateClose = () => {
        setUpdateDialog(false);
        setUpdateData(updateInitialState);
        setSelectedUser({
            id: null,
            role: null,
        });
        setUpdateMode("pwd");
    };
    const handleUpdate = async (id) => {
        if (isLoading) return;
        if (updateMode === "salary" && selectedUser.role === "rider") {
            if (!updateData.salary) {
                toast.warning("Salary is required");
                return;
            }
        }
        const payload = updateMode === "pwd"
            ? {
                updateMode,
            }
            : {
                updateMode,
                salary: Number(updateData.salary)
            };
        const encrypted = encryptPayload(payload);
        setIsLoading(true);
        try {
            const response = await api.post(`${process.env.REACT_APP_URI}/account/update-user/${id}`, { data: encrypted });
            if (response.data.success) {
                toast.success(response.data.message);
                if (updateMode === "pwd" && response.data.encryptedData) {
                    const decryptedData = decryptPayload(response.data.encryptedData);
                    await generateCredentialPDF(decryptedData);
                };
                handleUpdateClose();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error("Updating User failed:", error);
            toast.error(error?.response?.data?.message || "Error Updating User!");
        } finally {
            setIsLoading(false);
            return;
        }
    };

    // Details DialogBox with Api Call By ID 
    const [isViewOpen, setIsViewOpen] = useState(false);
    const [formData1, setFormData1] = useState({
        employee: null,
        guarantors: [],
    });
    const hasViewData = !!formData1?.employee || formData1?.guarantors?.length > 0;
    const handleViewOpen = async (id) => {
        setIsViewOpen(true);
        setLoaders(prev => ({ ...prev, details: true }));
        try {
            const response = await api.get(`${process.env.REACT_APP_URI}/account/detail/${id}`);
            if (response.data.success) {
                const decryptedData = decryptPayload(response.data.encryptedData);
                setFormData1({
                    employee: decryptedData.employee,
                    guarantors: decryptedData.guarantors || [],
                });
            }
            else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error('Error fetching user account details:', error);
        } finally {
            setLoaders(prev => ({ ...prev, details: false }));
            return;
        }
    };
    const handleViewClose = () => {
        setIsViewOpen(false);
        setFormData1({
            employee: null,
            guarantors: [],
        });
    };
    const fetchMediaBlob = async (url) => {
        try {
            const response = await api.get(`${mediaURI}${url}`, {
                responseType: "blob", // important for images
            });
            return URL.createObjectURL(response.data);
        } catch (err) {
            console.error("Error fetching media:", err);
            return null;
        }
    };
    const rolePriority = employeeMediaRoles.reduce((acc, item, index) => {
        acc[item.value] = index;
        return acc;
    }, {});
    const MediaGallery = ({ media = [] }) => {
        // Loader State for Fullscreen-View & Initial Image 
        const [mediaLoading, setMediaLoading] = useState({});
        const [viewerLoading, setViewerLoading] = useState(true);
        const handleMediaLoad = (url) => {
            setMediaLoading((prev) => ({ ...prev, [url]: { loading: false, error: false } }));
        };
        const handleMediaError = (url) => {
            setMediaLoading((prev) => ({ ...prev, [url]: { loading: false, error: true } }));
        };
        const [viewerImage, setViewerImage] = useState(null);

        const [mediaUrls, setMediaUrls] = useState({});
        useEffect(() => {
            media.forEach(async (m) => {
                if (!mediaUrls[m.url]) {
                    const blobUrl = await fetchMediaBlob(m.url);
                    setMediaUrls((prev) => ({ ...prev, [m.url]: blobUrl }));
                }
            });
            /* eslint-disable-next-line react-hooks/exhaustive-deps */
        }, [media]);

        if (!media.length) {
            return (
                <p className="text-sm text-gray-500 italic">
                    No documents uploaded
                </p>
            );
        };
        const sortedMedia = [...media].sort((a, b) => {
            const aOrder = rolePriority[a.role] ?? Number.MAX_SAFE_INTEGER;
            const bOrder = rolePriority[b.role] ?? Number.MAX_SAFE_INTEGER;
            return aOrder - bOrder;
        });
        return (
            <>
                {/* Thumbnails */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {sortedMedia.map((m, idx) => {
                        const ext = m.url?.split(".").pop()?.toLowerCase();
                        if (!["jpg", "jpeg", "png", "webp"].includes(ext)) return null;

                        const mediaState = mediaLoading[m.url] || { loading: true, error: false };
                        const blobUrl = mediaUrls[m.url];

                        return (
                            <div
                                key={idx}
                                className="relative w-full max-[370px]:w-full rounded-lg overflow-hidden bg-transparent"
                                style={{
                                    aspectRatio:
                                        m.metadata?.width && m.metadata?.height
                                            ? `${m.metadata.width} / ${m.metadata.height}`
                                            : "16 / 9",
                                    minHeight: 180,
                                }}
                            >
                                {/* Loader Layer */}
                                {mediaState.loading && !mediaState.error && (
                                    <div className="absolute inset-0 flex items-center justify-center backdrop-blur-sm bg-white/10 transition-opacity duration-500 z-10">
                                        <Loader />
                                    </div>
                                )}

                                {/* Error Overlay */}
                                {mediaState.error && (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black rounded-lg z-10">
                                        <Square2StackIcon className="w-10 h-10 text-red-500 mb-2" />
                                        <span className="text-red-400 font-semibold text-sm text-center px-2">
                                            Image unavailable
                                        </span>
                                    </div>
                                )}

                                {/* Image */}
                                <img
                                    src={blobUrl}
                                    alt={m.role}
                                    draggable={false}
                                    onLoad={() => handleMediaLoad(m.url)}
                                    onError={() => handleMediaError(m.url)}
                                    onClick={() => !mediaState.error && setViewerImage(blobUrl)}
                                    onContextMenu={(e) => e.preventDefault()}
                                    className={`w-full h-full object-cover rounded-lg cursor-pointer transition-all duration-700
                                    ${mediaState.loading || mediaState.error
                                            ? "scale-105 blur-sm opacity-70"
                                            : "scale-100 blur-0 opacity-100"
                                        }`}
                                />

                                {/* Role Label */}
                                <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded bg-black/60 text-white text-xs">
                                    {formatLabel(m.role)}
                                </div>
                            </div>
                        );
                    })}
                </div>
                {/* Fullscreen Image Viewer */}
                {viewerImage && createPortal(
                    <div
                        className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
                        onClick={() => {
                            setViewerImage(null);
                            setViewerLoading(true);
                        }}
                    >
                        <div className="relative max-h-[90vh] max-w-[90vw] flex items-center justify-center">
                            {/* Loader Overlay */}
                            {viewerLoading && (
                                <div className="absolute inset-0 flex items-center justify-center backdrop-blur-sm bg-white/10 rounded-lg transition-opacity duration-300">
                                    <Loader />
                                </div>
                            )}
                            {/* Image */}
                            <img
                                src={viewerImage}
                                alt="Full view"
                                onClick={(e) => e.stopPropagation()} // prevent close
                                onLoad={() => setViewerLoading(false)}
                                onContextMenu={(e) => e.preventDefault()}
                                className={`max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl transition-all duration-700 ${viewerLoading
                                    ? "scale-105 blur-sm opacity-70"
                                    : "scale-100 blur-0 opacity-100"
                                    }`}
                            />
                        </div>
                        {/* Close Button */}
                        <button
                            className="absolute top-5 right-5 bg-white/20 hover:bg-white/30 rounded-full p-2"
                            onClick={() => {
                                setViewerImage(null);
                                setViewerLoading(true);
                            }}
                        >
                            <XMarkIcon className="w-6 h-6 text-white" />
                        </button>
                    </div>,
                    document.body
                )}
            </>
        );
    };

    // Add DialogBox Code & Store API
    const initialFormState = {
        employeeRef: '',
        employeeObj: null,
        role: 'admin',
        salary: '',
    };
    const [formData, setFormData] = useState(initialFormState);
    const [isAddDialog, setAddDialog] = useState(false);
    const handleAddOpen = () => {
        setAddDialog(true);
    };
    const handleAddClose = () => {
        setAddDialog(false);
        setFormData(initialFormState);
    };
    const handleNumberChange = (e) => {
        const { name, value } = e.target;
        const digits = value.replace(/\D/g, "");
        setFormData(prev => ({ ...prev, [name]: digits }));
    };
    const handleSubmit = async () => {

        if (isLoading) return;

        // --- Required field checks ---
        if (!formData.role) {
            toast.warning("Role is required.");
            return;
        }
        if (formData.role === "rider" && !formData.employeeRef) {
            toast.warning("Employee is required.");
            return;
        }
        if (formData.role === "rider" && !formData.salary) {
            toast.warning("Salary is required.");
            return;
        }

        const payload = {
            role: formData.role,
            ...(formData.role === "rider" && { employeeRef: formData.employeeRef }),
            ...(formData.role === "rider" && { salary: Number(formData.salary) }),
        };
        const encryptedData = encryptPayload(payload);

        setIsLoading(true);
        try {
            const response = await api.post(`${process.env.REACT_APP_URI}/account/create`, { data: encryptedData });
            if (response.data.success) {
                toast.success(response.data.message);
                const decryptedData = decryptPayload(response.data.encryptedData);
                setAccountData((prevData) => [decryptedData, ...prevData]);
                await generateCredentialPDF(decryptedData);
                handleAddClose();
            }
            else {
                toast.error(response.data.message);
            }
        }
        catch (error) {
            console.log("User Account Creation failed:", error);
            if (error.response && error.response.data) {
                const data = error.response.data;
                if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
                    data.errors.forEach(err => toast.warning(err));
                } else {
                    toast.error(data.message);
                }
            } else {
                toast.error("Unable to Create User Account Network Error, please try again!");
            }
        }
        finally {
            setIsLoading(false);
            return;
        }
    };

    // Selector State handler
    const [selector, setSelector] = useState({
        open: false,
        type: null, // "employee"
        mode: "",
    });
    const openSelector = (type, mode) => {
        setSelector({ open: true, type, mode });
    };
    const handleEntitySelect = (item) => {
        const setter = selector.mode === "update" ? '' : setFormData;
        setter(prev => {
            if (selector.type === "employee") {
                return { ...prev, employeeRef: item._id, employeeObj: item };
            }
            return prev;
        });
    };

    // Generate OpCode States & API
    const [opCode, setOpCode] = useState(null);
    const [showOpDialog, setShowOpDialog] = useState(false);
    const copyOpCode = async () => {
        if (!opCode) return;
        await navigator.clipboard.writeText(opCode);
        toast.success("Copied");
    };
    const closeOpDialog = async () => {
        await copyOpCode();
        setOpCode(null);
        setShowOpDialog(false);
    };
    const handleGenerateOpCode = async () => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            const response = await api.post(`${process.env.REACT_APP_URI}/account/opcode`);
            if (response.data.success) {
                toast.success(response.data.message);
                const decryptedData = decryptPayload(response.data.encryptedData);
                setOpCode(decryptedData.code);
                setShowOpDialog(true);
            }
            else {
                toast.error(response.data.message);
            }
        }
        catch (err) {
            console.log("OpCode generation failed:", err);
            toast.error(err?.response.data.message || "Network Error Generating OpCode!");
        }
        finally {
            setIsLoading(false);
            return;
        }
    };

    // Action Btn API
    const handleAction = async (action, id) => {
        if (isLoading) return;

        let payload = {};
        if (action === "active" || action === "inactive") {
            payload = { isActive: action === "active" };
        } else if (action === "revoke") {
            payload = { revoke: true };
        }

        setIsLoading(true);
        try {
            const response = await api.post(`${process.env.REACT_APP_URI}/account/action/${id}`, payload);
            if (response.data.success) {
                const decryptedData = decryptPayload(response.data.encryptedData);
                setAccountData(prevData => prevData.map(data => data._id === id ? { ...data, ...decryptedData } : data));
                toast.success(response.data.message);
            } else {
                toast.warning(response.data.message);
            }
        } catch (err) {
            console.log(`Unable to ${action}:`, err);
            toast.error(err?.response?.data?.message || `Network Error Unable to ${action}`);
        } finally {
            setIsLoading(false);
            return;
        }
    };

    // Close menu list on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                showMenu &&
                attachmentRefs.current &&
                !attachmentRefs.current.contains(e.target)
            ) {
                setShowMenu(null);
            }
        };
        const closeOnKey = () => setShowMenu(null);
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", closeOnKey);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", closeOnKey);
        };
    }, [showMenu]);
    useEffect(() => {
        if (!menuState.open) return;
        const handleOutside = (e) => {
            const isTrigger = e.target.closest('[data-menu-trigger="true"]');
            const isMenu = e.target.closest('[data-floating-menu="true"]');
            if (isTrigger || isMenu) return;
            closeMenu();
        };
        document.addEventListener("mousedown", handleOutside);
        document.addEventListener("keydown", closeMenu);
        return () => {
            document.removeEventListener("mousedown", handleOutside);
            document.removeEventListener("keydown", closeMenu);
        };
    }, [menuState.open]);

    // actions btn list
    const menuActionList = [
        {
            label: "Activate", type: "active", color: "text-green-600", hover: "hover:bg-green-100",
            boxType: "warning", message: "This will activate the user account and allow access to portal. Do you want to continue?",
            showWhen: (data) => data?.isActive === false,
        },
        {
            label: "Deactivate", type: "inactive", color: "text-amber-600", hover: "hover:bg-amber-100",
            boxType: "warning", message: "This will deactivate the user account and restrict access to portal. Do you want to continue?",
            showWhen: (data) => data?.isActive === true,
        },
        {
            label: "Revoke Session", type: "revoke", color: "text-orange-600", hover: "hover:bg-orange-100",
            boxType: "danger", message: "Are you sure you want to revoke this user’s session? This action cannot be undone.",
            showWhen: (data) => data?.activeSessions > 0,
        },
        {
            label: "Edit", type: "edit", color: "text-blue-600", hover: "hover:bg-blue-100",
            boxType: "info", message: "Are you sure you want to edit this user account details?",
        },
        {
            label: "Delete", type: "delete", color: "text-red-600", hover: "hover:bg-red-100",
            boxType: "danger",
            message: "Are you sure you want to delete this user account along with all details and credential?"
        },
    ];

    return (
        <>
            <ConfirmDialog
                isOpen={dialog.open}
                onClose={() => setDialog((d) => ({ ...d, open: false }))}
                onConfirm={dialog.action}
                title={dialog.title}
                message={dialog.message}
                confirmText={dialog.confirmText}
                type={dialog.type}
            />
            <EntitySelector
                open={selector.open}
                onClose={() => setSelector({ open: false, type: null })}
                label={selector.type}
                apiPath={"/employee/selector-search"}
                displayFields={["name", "father_husband_name", "phone"]}
                onSelect={handleEntitySelect}
            />
            <OpCodeDialog
                isOpen={opCodeDialog.open}
                type="danger"
                title="Process Authorization"
                message="By proceeding, you accept responsibility for any resulting data loss or modification. Please enter the OpCode to continue."
                confirmText="Verify"
                onClose={() =>
                    setOpCodeDialog({ open: false, id: null })
                }
                onConfirm={(code) => {
                    handleDelete(opCodeDialog.id, code);
                }}
            />
            {/* Three Dot Menu UI */}
            {menuState.open && (
                <div className="fixed inset-0 z-50 pointer-events-none">
                    <div
                        data-floating-menu="true"
                        className="absolute bg-white border shadow-xl rounded-xl p-2 w-40 space-y-1 pointer-events-auto"
                        style={{
                            top: menuState.position.top,
                            left: menuState.position.left,
                        }}
                    >
                        {menuActionList.filter(action => action.showWhen ? action.showWhen(menuState.rowData) : true).map((action) => (
                            <button
                                key={action.type}
                                className={`flex items-center w-full gap-2 p-2 rounded-lg ${action.hover} text-sm font-medium cursor-pointer`}
                                onClick={() => {
                                    closeMenu();
                                    if (action.type === "active" || action.type === "inactive") {
                                        setDialog({
                                            open: true,
                                            type: action.boxType,
                                            title: action.label,
                                            message: action.message,
                                            confirmText: action.type === "active" ? "Activate" : "Deactivate",
                                            action: () => handleAction(action.type, menuState.rowId),
                                        });
                                        return;
                                    }
                                    if (action.type === "revoke") {
                                        setDialog({
                                            open: true,
                                            type: action.boxType,
                                            title: action.label,
                                            message: action.message,
                                            confirmText: "Revoke",
                                            action: () => handleAction(action.type, menuState.rowId),
                                        });
                                        return;
                                    }
                                    if (action.type === "edit") {
                                        setDialog({
                                            open: true,
                                            type: action.boxType,
                                            title: "Edit User",
                                            message: action.message,
                                            confirmText: "Proceed",
                                            action: () => setUpdateDialog(true),
                                        });
                                        return;
                                    }
                                    setDialog({
                                        open: true,
                                        type: action.boxType,
                                        title: "Delete Confirmation",
                                        message: action.message,
                                        confirmText: "Proceed",
                                        action: () => {
                                            setOpCodeDialog({
                                                open: true,
                                                id: menuState.rowId,
                                            });
                                        },
                                    });
                                }}
                            >
                                <span className={action.color}>{action.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
            {/* OpCode Display UI */}
            {showOpDialog && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50 px-3">
                    <div className="bg-white rounded-xl shadow-lg w-full max-w-sm p-4 sm:p-5">
                        <p className="text-md font-medium text-gray-500 mb-3 text-center">
                            Your current OpCode
                        </p>
                        <div className="flex items-center justify-center min-h-16 mb-4">
                            <span className="font-mono text-2xl sm:text-3xl font-semibold tracking-[0.3em] text-gray-800 text-center break-all">
                                {opCode}
                            </span>
                        </div>
                        <button
                            type="button"
                            className="w-full px-4 sm:px-6 py-2 rounded-lg text-sm sm:text-base font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                            onClick={closeOpDialog}
                        >
                            Copy
                        </button>
                    </div>
                </div>
            )}
            {/* Update UI */}
            {isUpdateDialog && (
                <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px] flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-lg shadow-lg overflow-hidden animate-fadeIn">
                        <div className={`bg-blue-600 text-white flex ${selectedUser.role === "admin" ? "justify-center" : "justify-between"} px-4 py-3`}>
                            <div className="font-semibold text-lg">
                                Update User
                            </div>
                            {selectedUser.role === "rider" && (
                                <button
                                    type="button"
                                    onClick={() => setUpdateMode(prev => prev === "pwd" ? "salary" : "pwd")}
                                    className="bg-white/20 hover:bg-white/30 px-2 p-1 rounded-full text-xs"
                                >
                                    {updateMode === "pwd" ? "Password" : "Salary"}
                                </button>
                            )}
                        </div>
                        <div className="p-3 sm:p-6 space-y-4">
                            {updateMode === "pwd" && (
                                <div className="space-y-4">
                                    <div className="text-center py-4">
                                        <div className="text-lg font-semibold text-gray-800"> Reset Password </div>
                                        <p className="mt-2 text-sm text-gray-500 leading-relaxed"> A new password will be generated automatically by the system. The current password will be replaced, and the new credentials will be downloaded as a PDF. </p>
                                    </div>
                                </div>
                            )}
                            {updateMode === "salary" && selectedUser.role === 'rider' && (
                                <div className="space-y-1">
                                    <label className="block text-sm font-medium text-gray-700">Salary</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                                            PKR
                                        </span>
                                        <input
                                            className="w-full pl-12 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                            placeholder="XX,XXX"
                                            type="text"
                                            name="salary"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            value={formatCurrency(updateData.salary)}
                                            onChange={onChangeUpdate}
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={handleUpdateClose}
                                    className="px-4 sm:px-6 py-2 rounded-lg text-sm sm:text-base bg-gray-200 text-gray-700 hover:bg-gray-300 transition"
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    className="px-4 sm:px-6 py-2 rounded-lg text-sm sm:text-base bg-blue-600 text-white hover:bg-blue-700 transition"
                                    onClick={() => {
                                        setDialog({
                                            open: true,
                                            type: "warning",
                                            title: updateMode === "pwd" ? "Generate Password" : "Confirm Update",
                                            message: updateMode === "pwd"
                                                ? "A new password will be generated and the current password will be replaced with the new one. This cannot be undone."
                                                : "Want to update details for this user account?",
                                            confirmText: updateMode === "pwd" ? "Generate" : "Update",
                                            action: () => handleUpdate(selectedUser.id),
                                        })
                                    }}
                                >
                                    {updateMode === "pwd" ? "Generate" : "Update"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Details UI */}
            {isViewOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-[2px] z-40 p-3">
                    <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto animate-fadeIn border border-gray-200">
                        <div className="max-h-[85vh] flex flex-col">
                            {/* Header */}
                            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3 sm:p-4 shadow-md text-center">
                                <h2 className="text-xl sm:text-2xl font-bold">Employee Details</h2>
                                <p className="text-[10px] sm:text-xs opacity-80">Smart by Logic. Built for You.</p>
                            </div>
                            {loaders.details ? (
                                <div className="flex justify-center items-center h-[45vh]">
                                    <Loader />
                                </div>
                            ) : !hasViewData ? (
                                <FallBack
                                    title="Employee details not available"
                                    message="This Employee record could not be loaded. It may have been removed, or there was a network issue."
                                    actionText="Close"
                                    onAction={handleViewClose}
                                />
                            ) : (
                                <div className="flex-1 space-y-4 overflow-auto px-2.5 sm:px-4 py-5 bg-gray-50">
                                    {formData1.employee && (
                                        <div className="space-y-5">
                                            {/* Name Row */}
                                            <div className="flex items-center gap-4">
                                                <div className="w-16 h-16 rounded-full bg-blue-600 text-white flex items-center justify-center text-xl font-semibold">
                                                    {formData1.employee.name?.charAt(0)}
                                                </div>

                                                <div className="min-w-0">
                                                    <p className="text-lg font-semibold text-gray-900 truncate">
                                                        {formData1.employee.name}
                                                    </p>
                                                    <p className="text-sm text-gray-500 truncate">
                                                        ID · {formData1.employee.employeeId}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Info grid */}
                                            <div className="pt-4 border-t grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8 text-[15px]">
                                                {[
                                                    ["Category", formatLabel(formData1.employee.category)],
                                                    ["Date", formatDateTime(formData1.employee.date)],
                                                    ["Duty Time", formatLabel(formData1.employee.duty_time)],
                                                    ["Gender", formatLabel(formData1.employee.gender)],
                                                    ["Age", `${formData1.employee.age} year`],
                                                    ["Phone", formData1.employee.phone],
                                                    ["CNIC", formatCnic(formData1.employee.cnic_no)],
                                                    ["Education", formatLabel(formData1.employee.education)],
                                                    ["Experience", `${formData1.employee.experience} year`],
                                                    ["Father / Husband", formData1.employee.father_husband_name],
                                                    ["Grandfather", formData1.employee.grandfather_name],
                                                    ["Parent Status", formatLabel(formData1.employee.parent_status)],
                                                    ["Caste", formData1.employee.caste],
                                                    ["Religion", formatLabel(formData1.employee.religion)],
                                                    ["Marital Status", formatLabel(formData1.employee.married_status)],
                                                    ["Children", formData1.employee.kid_amount],
                                                    ["Children Gender", formatLabel(formData1.employee.kid_gender)],
                                                    ["Children Age", formData1.employee.kid_age],
                                                    ["Salary", `PKR ${formatCurrency(formData1.employee.salary)}`],
                                                ].map(([label, value]) => (
                                                    <div key={label}>
                                                        <p className="text-gray-400 text-sm">{label}</p>
                                                        <p className="text-gray-900 font-medium">{value || "—"}</p>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Known Place & Address */}
                                            <div className='grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8'>
                                                <div>
                                                    <p className="text-sm text-gray-400 mb-1">Known Place</p>
                                                    <p className="text-[15px] text-gray-900 leading-relaxed">
                                                        {formData1.employee.known_place}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-400 mb-1">Address</p>
                                                    <p className="text-[15px] text-gray-900 leading-relaxed">
                                                        {formData1.employee.address}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Media */}
                                            <div className="pt-4 border-t py-3">
                                                <p className="text-base font-medium text-gray-800 mb-3">
                                                    Documents
                                                </p>
                                                <MediaGallery media={formData1.employee.media} />
                                            </div>
                                        </div>
                                    )}
                                    {formData1.guarantors.length > 0 && (
                                        <div className="pt-4 border-t space-y-4">
                                            <p className="text-base font-semibold text-gray-900">
                                                Guarantors
                                            </p>
                                            {formData1.guarantors.map((g, i) => (
                                                <div
                                                    key={g._id}
                                                    className="rounded-xl border border-gray-200 bg-white p-4 space-y-4"
                                                >
                                                    {/* Title */}
                                                    <div>
                                                        <p className="text-sm font-semibold text-gray-900">
                                                            Guarantor #{i + 1}
                                                        </p>
                                                    </div>

                                                    {/* Info */}
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8 text-[15px]">
                                                        {[
                                                            ["Name", g.name],
                                                            ["Father / Husband", g.father_husband_name],
                                                            ["Gender", formatLabel(g.gender)],
                                                            ["Age", `${g.age} year`],
                                                            ["CNIC", formatCnic(g.cnic_no)],
                                                            ["Phone", g.phone],
                                                            ["Relation", formatLabel(g.relation)],
                                                            ["Religion", formatLabel(g.religion)],
                                                            ["Caste", g.caste],
                                                            ["Occupation", g.occupation],
                                                        ].map(([label, value]) => (
                                                            <div key={label}>
                                                                <p className="text-gray-400 text-sm">{label}</p>
                                                                <p className="text-gray-900 font-medium">{value || "—"}</p>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    {/* Address */}
                                                    <div>
                                                        <p className="text-sm text-gray-400 mb-1">Address</p>
                                                        <p className="text-[15px] text-gray-900 leading-relaxed">
                                                            {g.address || "—"}
                                                        </p>
                                                    </div>

                                                    {/* Media */}
                                                    <MediaGallery media={g.media} />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* Btn */}
                            {hasViewData && (
                                <div className="border-t p-2.5 sm:px-4 sm:py-2.5 flex items-center justify-between">
                                    <span className='text-xs'>
                                        <span className="font-medium text-gray-600">Updated At:</span>{" "}
                                        {formatTableDateTime(formData1.employee.updatedAt)}
                                    </span>
                                    <button
                                        type='button'
                                        onClick={() => handleViewClose()}
                                        className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base bg-blue-600 hover:bg-blue-500 text-white font-semibold transition"
                                    >
                                        Close
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <div className="w-full">
                {/* Heading */}
                <div className="w-full mb-5 flex flex-wrap items-center justify-between gap-2 sm:gap-4">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                        <button
                            className="md:hidden p-2 bg-white shadow rounded-lg"
                            onClick={() => setIsMobileOpen(true)}
                        >
                            <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-800" />
                        </button>
                        <div className="flex flex-wrap items-center">
                            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-wide text-blue-600">
                                Acco
                            </h1>
                            <span className="text-2xl sm:text-3xl font-extrabold tracking-wide text-gray-800">
                                unt
                            </span>
                        </div>
                    </div>

                    <div className='flex items-center gap-2'>
                        <button
                            className="px-4 sm:px-6 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-500 font-medium text-sm sm:text-base"
                            onClick={handleAddOpen}
                        >
                            Add
                        </button>
                        {isAddDialog && (
                            <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-[2px] z-40 p-3">
                                <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto animate-fadeIn border border-gray-200">
                                    <div className="max-h-[85vh] flex flex-col">
                                        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3 sm:p-4 shadow-md text-center">
                                            <h2 className="text-xl sm:text-2xl font-bold">Create User</h2>
                                            <p className="text-[10px] sm:text-xs opacity-80">Smart by Logic. Built for You.</p>
                                        </div>
                                        <div className="flex-1 overflow-auto p-3 sm:p-6 bg-gray-50">
                                            <fieldset className="bg-white border p-4 rounded-lg">
                                                <legend className="text-md sm:text-lg font-semibold text-gray-700 px-2">Info</legend>
                                                <div className="grid grid-cols-1 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Role
                                                        </label>
                                                        <div className="flex rounded-full bg-gray-200 p-1 w-fit">
                                                            {userRoleOptions.map((option) => {
                                                                const isActive = formData.role === option.value;
                                                                return (
                                                                    <button
                                                                        key={option.value}
                                                                        type="button"
                                                                        onClick={() =>
                                                                            setFormData((prev) => ({
                                                                                ...prev,
                                                                                role: option.value,
                                                                            }))
                                                                        }
                                                                        className={`px-4 py-1 text-sm rounded-full transition ${isActive ? "bg-white shadow text-black" : "text-gray-600"
                                                                            }`}
                                                                    >
                                                                        {option.label}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                    {formData.role === 'rider' && (
                                                        <button
                                                            type="button"
                                                            onClick={() => openSelector("employee", "create")}
                                                            className="w-full flex items-center justify-between px-4 py-3 border rounded-xl bg-white hover:bg-gray-50 transition shadow-sm"
                                                        >
                                                            <div className="flex items-center gap-3 min-w-0">
                                                                <UserIcon className="h-5 w-5 text-blue-600" />
                                                                <div className="flex flex-col text-left min-w-0">
                                                                    <span className="text-xs text-gray-500">Employee</span>
                                                                    <span className="text-sm font-medium text-gray-800 truncate">
                                                                        {formData.employeeObj?.name || "Select Employee"}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <span className="text-xs text-gray-400">
                                                                {formData.employeeRef ? "Change" : ""}
                                                            </span>
                                                        </button>
                                                    )}
                                                    {formData.role === 'rider' && (
                                                        <div className="space-y-1">
                                                            <label className="block text-sm font-medium text-gray-700">Salary</label>
                                                            <div className="relative">
                                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                                                                    PKR
                                                                </span>
                                                                <input
                                                                    className="w-full pl-12 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                                                    placeholder="XX,XXX"
                                                                    type="text"
                                                                    name="salary"
                                                                    inputMode="numeric"
                                                                    pattern="[0-9]*"
                                                                    value={formatCurrency(formData.salary)}
                                                                    onChange={handleNumberChange}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </fieldset>
                                        </div>
                                        <div className="border-t p-2.5 sm:px-4 sm:py-2.5 flex justify-between">
                                            <button
                                                type='button'
                                                onClick={() => handleAddClose()}
                                                className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 transition"
                                            >
                                                Close
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleSubmit}
                                                className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base bg-blue-600 text-white hover:bg-blue-700 transition"
                                            >
                                                Create
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={attachmentRefs} className="relative flex-shrink-0">
                            <button
                                type="button"
                                onClick={(e) => setShowMenu(prev => !prev)}
                                className="p-2 rounded-full hover:bg-gray-100"
                            >
                                <EllipsisVerticalIcon className="h-6 w-6 text-gray-600" />
                            </button>
                            {showMenu && (
                                <div className="absolute top-10 right-0 bg-white border shadow-lg rounded-xl p-2 w-44 z-10 space-y-3">
                                    <div className="flex flex-col space-y-1">
                                        <button
                                            className='w-full text-left px-2 py-1 rounded-lg text-sm font-medium transition text-orange-600 hover:bg-orange-100'
                                            onClick={() => {
                                                setDialog({
                                                    open: true,
                                                    type: "warning",
                                                    title: "Generate OpCode",
                                                    message: "This will generate a new OpCode used to confirm permanent and system-critical actions. The new OpCode will replace any existing one. Do you want to continue?",
                                                    confirmText: "Generate",
                                                    action: () => handleGenerateOpCode(),
                                                })
                                            }}
                                        >
                                            OpCode
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <hr className="border-t border-gray-200 my-4" />

                {/* Data Display Code */}
                <div className="w-full overflow-x-auto h-[70vh]">
                    {(loaders.fetch) ? (
                        <div className="flex justify-center items-center h-full p-6">
                            <Loader />
                        </div>
                    ) : paginatedData.length === 0 ? (
                        <div className="flex justify-center items-center h-full p-6">
                            <p className="font-semibold text-gray-600 text-xl sm:text-md">
                                No User !!
                            </p>
                        </div>
                    ) : (
                        <>
                            <table className="w-full bg-white">
                                <thead className="bg-gray-50">
                                    <tr>
                                        {[
                                            "#",
                                            "ID",
                                            "Username",
                                            "Role",
                                            "Status",
                                            "Sessions",
                                            "Last Login",
                                            "Action",
                                        ].map((header, index) => (
                                            <th
                                                key={index}
                                                className="p-3 text-sm font-semibold text-gray-600 text-center border-b-2 border-gray-200"
                                            >
                                                {header}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>

                                <tbody>
                                    {paginatedData.map((data, index) => (
                                        <tr key={data._id || index} className="hover:bg-gray-50 even:bg-gray-100">
                                            <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                {(currentPage - 1) * itemsPerPage + index + 1}
                                            </td>
                                            <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                {data.userId}
                                            </td>
                                            <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                {data.username}
                                            </td>
                                            <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                {formatLabel(data.role)}
                                            </td>
                                            <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                <div className="flex items-center justify-center gap-2">
                                                    <span
                                                        className={`h-2.5 w-2.5 rounded-full ${data.isActive ? 'bg-green-500' : 'bg-red-500'
                                                            }`}
                                                    ></span>
                                                    <span>{data.isActive ? 'Active' : 'Inactive'}</span>
                                                </div>
                                            </td>
                                            <td className="p-3 text-sm text-center border-b">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium
                                                        ${data.activeSessions === 0
                                                        ? "bg-red-100 text-red-800"
                                                        : "bg-green-100 text-green-800"
                                                    }`}>
                                                    {data.activeSessions ?? 0}
                                                </span>
                                            </td>
                                            <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                                                {formatTableDateTime(data.lastLoginAt)}
                                            </td>
                                            <td className="p-3 text-center border-b border-gray-200">
                                                <div className="flex justify-center space-x-2">
                                                    {data.role === "rider" && (
                                                        <button
                                                            type='button'
                                                            className="px-2 sm:px-4 py-2 rounded-xl bg-blue-500 text-white hover:bg-blue-600 font-medium text-sm sm:text-base"
                                                            onClick={() => {
                                                                setDialog({
                                                                    open: true,
                                                                    type: "info",
                                                                    title: "View Details",
                                                                    message: "Want to view details for this employee?",
                                                                    confirmText: "View",
                                                                    action: () => handleViewOpen(data._id),
                                                                })
                                                            }}
                                                        >
                                                            Details
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        data-menu-trigger="true"
                                                        className="p-2 rounded-full hover:bg-gray-100 cursor-pointer"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (menuState.open && menuState.rowId === data._id) {
                                                                closeMenu();
                                                                return;
                                                            }
                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                            openMenu({
                                                                id: data._id,
                                                                row: data,
                                                                position: {
                                                                    top: rect.bottom + window.scrollY - (data.activeSessions > 0 ? 170 : 130),
                                                                    left: rect.right + window.scrollX - 200,
                                                                },
                                                            });
                                                            setSelectedUser({ id: data._id, role: data.role });
                                                        }}
                                                    >
                                                        <EllipsisVerticalIcon className="h-6 w-6 text-gray-600" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}

                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="w-full flex flex-col sm:flex-row justify-between items-center p-4 gap-2 sm:gap-4">
                        {/* Showing Entries Text */}
                        <span className="text-sm text-gray-600 text-center sm:text-left">
                            Showing {startIndex} to {endIndex} of {totalBackendPg} entries
                        </span>
                        {/* Pagination Buttons */}
                        <div className="flex flex-wrap justify-center sm:justify-end gap-1 sm:gap-2">
                            {/* Previous Button */}
                            <button
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronLeftIcon />
                            </button>
                            {/* Page Numbers */}
                            {(() => {
                                const buttons = [];
                                const pageRange = 1; // current ±2 pages
                                let startPage = Math.max(currentPage - pageRange, 1);
                                let endPage = Math.min(currentPage + pageRange, totalPages);

                                if (startPage > 1) {
                                    buttons.push(
                                        <button
                                            key={1}
                                            onClick={() => setCurrentPage(1)}
                                            className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${currentPage === 1 ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"}`}
                                        >
                                            1
                                        </button>
                                    );
                                    if (startPage > 2) buttons.push(<span key="start-ellipsis" className="px-2 hidden sm:inline">...</span>);
                                }

                                for (let i = startPage; i <= endPage; i++) {
                                    buttons.push(
                                        <button
                                            key={i}
                                            onClick={() => setCurrentPage(i)}
                                            className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${currentPage === i ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"}`}
                                        >
                                            {i}
                                        </button>
                                    );
                                }

                                if (endPage < totalPages) {
                                    if (endPage < totalPages - 1) buttons.push(<span key="end-ellipsis" className="px-2 hidden sm:inline">...</span>);
                                    buttons.push(
                                        <button
                                            key={totalPages}
                                            onClick={() => setCurrentPage(totalPages)}
                                            className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${currentPage === totalPages ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"}`}
                                        >
                                            {totalPages}
                                        </button>
                                    );
                                }
                                return buttons;
                            })()}
                            {/* Next Button */}
                            <button
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronRightIcon />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    )
}

export default Account;